import React, { useRef, useState, useSyncExternalStore } from "react";
import {
    Dialog,
    Form,
    Modal,
    ModalOverlay,
    VisuallyHidden,
} from "react-aria-components";
import { t } from "ttag";

import { GatingFormFieldsStream, reverse } from "@reactivated";

import { slugify } from "@thelabnyc/thelabui/src/utils/text";

import { datalayerPush } from "../../utils/analytics";
import { Clickable } from "../Clickables";
import { useFormSubmit } from "../Form";
import { Svg } from "../Svg";
import { FormFields } from "./FormFields";

import styles from "./dialog.module.scss";

const alEvent = (
    interaction: "start" | "submit" | "error" | "success",
    title: string,
    id: string | number,
) =>
    datalayerPush({
        event: "form_interaction",
        form_interaction: `form_${interaction}`, // form_start | form_submit | form_error | form_success
        form_title: title,
        form_ref: id, // form ID or other reference
    });

interface SoftPageGateProps {
    formId?: number;
    fields?: GatingFormFieldsStream;
    /** Override cookie key. Defaults to String(formId). Only set in tests/Storybook. */
    gateGroup?: string;
}

function hasCookie(name: string): boolean {
    return document.cookie
        .split(";")
        .some((c) => c.trim().startsWith(`${name}=`));
}

function setGateCookie(name: string): void {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `${name}=1; expires=${expires.toUTCString()}; path=/`;
}

interface GatingFormDialogProps {
    isOpen: boolean;
    label: string;
    children: React.ReactNode;
}

export function GatingFormDialog({
    isOpen,
    label,
    children,
}: GatingFormDialogProps) {
    return (
        <ModalOverlay
            className={styles.overlay}
            isOpen={isOpen}
            isDismissable={false}
            isKeyboardDismissDisabled={true}
        >
            <Modal className={styles.modal}>
                <Dialog
                    role="alertdialog"
                    className={styles.dialog}
                    aria-label={label}
                >
                    {children}
                </Dialog>
            </Modal>
        </ModalOverlay>
    );
}

const noopSubscribe = () => () => {};

const FORM_TITLE_FALLBACK = "registration";

const extractFormTitle = (
    fields: GatingFormFieldsStream | undefined,
): string => {
    const featured = fields?.find((b) => b.type === "featured_richtext");
    const heading = featured?.value.stream?.find((b) => b.type === "heading");
    if (!heading) return FORM_TITLE_FALLBACK;
    const text = heading.value.text?.blocks.map((b) => b.text).join(" ") ?? "";
    return slugify(text) || FORM_TITLE_FALLBACK;
};

export function SoftPageGate(props: SoftPageGateProps) {
    const cookieName = props.gateGroup ?? String(props.formId ?? "gate");
    const formRef: string | number = props.formId ?? cookieName;
    const formTitle = extractFormTitle(props.fields);
    const [dismissed, setDismissed] = useState(false);
    const startedRef = useRef(false);
    const invalidFiredRef = useRef(false);

    const isGated = useSyncExternalStore(
        noopSubscribe,
        () => !hasCookie(cookieName),
        () => false,
    );
    const isOpen = isGated && !dismissed;

    const { isSubmitting, nonFieldError, fieldErrors, handleSubmit } =
        useFormSubmit(
            async (data) => {
                if (props.formId === undefined) return null;
                alEvent("submit", formTitle, formRef);
                try {
                    const response = await fetch(
                        reverse("gating-form-submit", { pk: props.formId }),
                        {
                            method: "POST",
                            body: data,
                            headers: [["Accept", "application/json"]],
                        },
                    );
                    if (!response.ok) {
                        alEvent("error", formTitle, formRef);
                    }
                    return response;
                } catch (e) {
                    alEvent("error", formTitle, formRef);
                    throw e;
                }
            },
            () => {
                alEvent("success", formTitle, formRef);
                setGateCookie(cookieName);
                setDismissed(true);
            },
        );

    const handleFormFocus = () => {
        if (startedRef.current) return;
        startedRef.current = true;
        alEvent("start", formTitle, formRef);
    };

    // Fires once per invalid field when client-side validation blocks
    // submission. Dedupe per attempt since multiple fields can fail at once.
    const handleInvalid = () => {
        if (invalidFiredRef.current) return;
        invalidFiredRef.current = true;
        alEvent("submit", formTitle, formRef);
        alEvent("error", formTitle, formRef);
        queueMicrotask(() => {
            invalidFiredRef.current = false;
        });
    };

    return (
        <GatingFormDialog isOpen={isOpen} label={t`Registration required`}>
            {/* Close navigates back — this gate blocks page access until the form is submitted */}
            <Clickable
                onClick={() => window.history.back()}
                className={styles.close}
            >
                <VisuallyHidden>{t`Close`}</VisuallyHidden>
                <Svg aria-hidden name="close" />
            </Clickable>
            <Form
                onSubmit={handleSubmit}
                onInvalid={handleInvalid}
                validationErrors={fieldErrors}
            >
                {nonFieldError && <p role="alert">{nonFieldError}</p>}
                <fieldset disabled={isSubmitting} onFocus={handleFormFocus}>
                    {props.fields && <FormFields value={props.fields} />}
                </fieldset>
            </Form>
        </GatingFormDialog>
    );
}

export { FormFields };
